import { lumi } from "../lib/lumi"
import { isRateLimitError } from "./rateLimit"
import { isFavoritesCacheFresh, readFavoritesCache, writeFavoritesCache } from "./favoritesCache"

type FavoritesFetchResult = {
  productIds: string[]
  rateLimited: boolean
}

const DEFAULT_TTL_MS = 30_000

// 去重/合併：同一個 userEmail 同時只允許一個 in-flight request
const inFlightByEmail = new Map<string, Promise<FavoritesFetchResult>>()

// 若遇到 429，進入冷卻期：冷卻期間不再打 API，直接 fallback cache/[]
const rateLimitedUntilByEmail = new Map<string, number>()

function getCooldownUntil(email: string): number {
  return rateLimitedUntilByEmail.get(email) ?? 0
}

function setCooldown(email: string, cooldownMs: number): void {
  rateLimitedUntilByEmail.set(email, Date.now() + cooldownMs)
}

export async function fetchFavoritesForUser(
  userEmail: string,
  options?: { ttlMs?: number; cooldownMsOn429?: number }
): Promise<FavoritesFetchResult> {
  const ttlMs = options?.ttlMs ?? DEFAULT_TTL_MS
  const cooldownMsOn429 = options?.cooldownMsOn429 ?? 60_000

  // 1) 快取命中 → 直接回傳，避免任何 API
  const cache = readFavoritesCache()
  if (isFavoritesCacheFresh(cache, ttlMs)) {
    return { productIds: cache?.productIds ?? [], rateLimited: false }
  }

  // 2) 429 冷卻期 → 不打 API，直接 fallback（顯示 0 / 隱藏區塊）
  if (Date.now() < getCooldownUntil(userEmail)) {
    return { productIds: [], rateLimited: true }
  }

  // 3) 去重/合併：共用 in-flight
  const existing = inFlightByEmail.get(userEmail)
  if (existing) return existing

  const p = (async (): Promise<FavoritesFetchResult> => {
    try {
      const result = await lumi.entities.favorites.list({
        filter: { userEmail },
        limit: 1000,
      })

      const ids = (result?.list ?? [])
        .map((fav: any) => fav?.productId)
        .filter(Boolean)

      writeFavoritesCache(ids)
      return { productIds: ids, rateLimited: false }
    } catch (error) {
      // 429：rate limit 行為 → 不重試、不噴錯；進入冷卻期並 fallback
      if (isRateLimitError(error)) {
        setCooldown(userEmail, cooldownMsOn429)
        // 429 是 rate limit 行為：不重試、不噴錯，直接 fallback（顯示 0 / 隱藏區塊）
        return { productIds: [], rateLimited: true }
      }

      // 其他錯誤：保留 console.error 以利排查，但仍需 fallback，避免 UI 卡死
      console.error("Failed to fetch favorites:", error)
      return { productIds: readFavoritesCache()?.productIds ?? [], rateLimited: false }
    } finally {
      // 確保 in-flight 一定清除，避免永遠 pending 造成後續都卡住
      inFlightByEmail.delete(userEmail)
    }
  })()

  inFlightByEmail.set(userEmail, p)
  return p
}

export async function fetchFavoritesCountForUser(
  userEmail: string,
  options?: { ttlMs?: number; cooldownMsOn429?: number }
): Promise<{ count: number; rateLimited: boolean }> {
  const res = await fetchFavoritesForUser(userEmail, options)
  return { count: res.productIds.length, rateLimited: res.rateLimited }
}
